import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { ListItem } from "@sortera/react-components";
import { CheckIcon, ChevronUpDownIcon, LightBulbIcon } from "@sortera/react-icons";
import type { AgentModelDto } from "../../types/index.ts";
import styles from "./model-picker.module.css";

/**
 * Which model answers: a chip in the message box that opens a short list upwards. Built like the
 * project switcher, for the same reason — the design system's SelectField is a labelled form
 * field, heavier than a toolbar wants.
 */
export function ModelPicker({
  models,
  model,
  onChange,
}: {
  models: AgentModelDto[];
  model: string | null;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const current = models.find((m) => m.id === model) ?? models[0];

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    root.current?.querySelector<HTMLButtonElement>(`[data-model="${CSS.escape(model ?? "")}"]`)?.focus();
    return () => window.removeEventListener("pointerdown", onDown);
  }, [open, model]);

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(root.current?.querySelectorAll<HTMLButtonElement>("[data-model]") ?? []);
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      trigger.current?.focus();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      items[(at + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  if (models.length < 2 || !current) return null;

  return (
    <div className={styles.root} ref={root} onClick={(e) => e.stopPropagation()}>
      <button
        ref={trigger}
        type="button"
        className={styles.trigger}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        title="Välj vilken modell som svarar"
        onClick={() => setOpen((o) => !o)}
      >
        <LightBulbIcon size={14} />
        {current.label}
        <ChevronUpDownIcon size={14} />
      </button>
      {open && (
        <div className={styles.menu} id={menuId} role="group" aria-label="Välj modell" onKeyDown={onMenuKey}>
          {models.map((m) => (
            <ListItem
              key={m.id}
              size="desktop"
              data-model={m.id}
              aria-current={m.id === current.id || undefined}
              trailingIcon={m.id === current.id ? <CheckIcon /> : undefined}
              onClick={() => {
                setOpen(false);
                onChange(m.id);
                trigger.current?.focus();
              }}
            >
              <span className={styles.option}>
                <span className={styles.label}>{m.label}</span>
                {m.hint && <span className={styles.hint}>{m.hint}</span>}
              </span>
            </ListItem>
          ))}
        </div>
      )}
    </div>
  );
}
